import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResourceData } from '@app/shared/abstract/resource.data';
import {
  ClientAccessInvitationModel,
  ClientAccessSetPasswordModel,
  ClientAccessStateModel,
} from './client-access.model';

/**
 * TVG-43: HTTP wrapper for the Client Access endpoints.
 *
 * The staff endpoints are gated behind the `crm-sales-flow` feature toggle on
 * the server (404 when off); callers should still check the toggle locally
 * before rendering UI. Each staff endpoint returns the full state so the panel
 * can refresh from every response.
 *
 * The two `customer/*` endpoints are unauthenticated and back the invitation
 * email link: validate the token, then set the password and receive a Sanctum
 * bearer token to log the contact in.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientAccessService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getState(eventId: number | string): Observable<ResourceData<ClientAccessStateModel>> {
    return this.http.get<ResourceData<ClientAccessStateModel>>(
      `${this.apiUrl}/api/events/${eventId}/client-access`,
    );
  }

  enable(eventId: number | string): Observable<ResourceData<ClientAccessStateModel>> {
    return this.http.post<ResourceData<ClientAccessStateModel>>(
      `${this.apiUrl}/api/events/${eventId}/client-access/enable`,
      {},
    );
  }

  disable(eventId: number | string): Observable<ResourceData<ClientAccessStateModel>> {
    return this.http.post<ResourceData<ClientAccessStateModel>>(
      `${this.apiUrl}/api/events/${eventId}/client-access/disable`,
      {},
    );
  }

  resendInvitation(
    eventId: number | string,
    contactId: number | string,
  ): Observable<ResourceData<ClientAccessStateModel>> {
    return this.http.post<ResourceData<ClientAccessStateModel>>(
      `${this.apiUrl}/api/events/${eventId}/client-access/contacts/${contactId}/resend-invitation`,
      {},
    );
  }

  sendPasswordReset(
    eventId: number | string,
    contactId: number | string,
  ): Observable<ResourceData<ClientAccessStateModel>> {
    return this.http.post<ResourceData<ClientAccessStateModel>>(
      `${this.apiUrl}/api/events/${eventId}/client-access/contacts/${contactId}/send-password-reset`,
      {},
    );
  }

  // TVG-43: public — validate the invitation token from the email link.
  // Returns HTTP 410 when the token is invalid/expired/used.
  validateInvitation(token: string): Observable<ResourceData<ClientAccessInvitationModel>> {
    return this.http.post<ResourceData<ClientAccessInvitationModel>>(
      `${this.apiUrl}/api/customer/client-access/invitation`,
      { token },
      { withCredentials: true },
    );
  }

  // TVG-43: public — set the password against an invitation token. Returns a
  // Sanctum bearer token to store and use as the logged-in customer.
  setPassword(
    token: string,
    password: string,
    passwordConfirmation: string,
  ): Observable<ResourceData<ClientAccessSetPasswordModel>> {
    return this.http.post<ResourceData<ClientAccessSetPasswordModel>>(
      `${this.apiUrl}/api/customer/client-access/set-password`,
      { token, password, password_confirmation: passwordConfirmation },
      { withCredentials: true },
    );
  }
}
